import { Injectable, NgModule } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes, TitleStrategy } from '@angular/router';
import { bareRouteMatchGuard } from '../app/guards/bare-route-match-guard';
import { orgScopedRouteMatchGuard } from '../app/guards/org-scoped-route-match-guard';
import { orgUidValidatorGuard } from '../app/guards/org-uid-validator-guard';
import { canMatchFeatureEnabled, isFeaturePolicyEnabled } from '../app/guards/feature-flag-guard';
import { hasPermissionGuard } from '../app/guards/permission-guard';
import { downloadCenterAccessGuard } from '../download-center/download-center-access.guard';
import { platformOrgPrimerGuard } from '../app/guards/platform-org-primer-guard';

import { PageWrapperComponent } from './page-wrapper/page-wrapper.component';
import { EmptyPageComponent } from './empty-page/empty-page.component';
import { FeatureNames } from '../model/FeatureNames';
import { PureTeam } from '../utils/teamOwnership';

/**
 * Shared children for the two PageWrapper parent routes: `orgs/:orgUid` and `''`.
 * Same array reference is intentional — Angular keys child nodes by parent path
 * and never mutates Route.data during matching. Do NOT push/splice this array in
 * place; build a new array if you need to vary it per parent.
 *
 * Routes flagged `data.orgScoped === false` are not org-scoped. `bareRouteMatchGuard`
 * uses this to skip the `/orgs/<uid>` redirect for those paths.
 */
export const PAGE_WRAPPER_CHILDREN: Routes = [
    {
        path: 'administration',
        loadChildren: () => import('../administration/administration.module').then(mod => mod.AdministrationModule),
    },
    {
        path: 'assets',
        loadChildren: () => import('../asset-management/asset-management.routes').then(mod => mod.routes),
    },
    {
        path: 'fleet',
        loadChildren: () => import('../fleet/fleet.module').then(mod => mod.FleetModule),
    },
    {
        path: 'messages',
        loadChildren: () => import('../messages/messages.module').then(mod => mod.MessagesModule),
    },
    {
        path: 'analysis',
        loadChildren: () => import('../analytics/analytics.module').then(mod => mod.AnalyticsModule),
    },
    {
        path: 'protection',
        loadChildren: () => import('../protection/protection.routes').then(mod => mod.routes),
    },
    {
        path: 'data-intelligence',
        loadChildren: () => import('../data-intelligence/deployments/deployments.routes').then(mod => mod.routes),
    },
    {
        path: 'dashboard',
        loadChildren: () => import('../dashboard/dashboard.module').then(mod => mod.DashboardModule),
    },
    {
        path: 'workflows',
        loadChildren: () => import('../workflow/workflow.module').then(mod => mod.WorkflowModule),
    },
    {
        // Workflow Engine connection management. `canMatch`, not `canActivate`:
        // with the flag off the route matches nothing and the URL falls through
        // to the `**` catch-all, rather than activating the page and then
        // redirecting away from it. The flag is the only gate here, and
        // AUTOMATION_ROUTES adds none: connector-service enforces
        // `PURE1:read:workflow_connections` on every read, and the page renders
        // that refusal rather than bouncing somebody who cannot read.
        path: 'automation',
        loadChildren: () => import('../automation/automation.routes').then(m => m.AUTOMATION_ROUTES),
        canMatch: [canMatchFeatureEnabled(FeatureNames.WORKFLOW_ENGINE_POC)],
    },
    {
        path: 'resilience',
        loadChildren: () =>
            import('../disaster-recovery/disaster-recovery.module').then(mod => mod.DisasterRecoveryModule),
    },
    {
        // Legacy redirect: preserve sub-paths from /draas/** to /resilience/**
        path: 'draas',
        redirectTo: 'resilience',
    },
    {
        path: 'services',
        loadChildren: () => import('../service-catalog/service-catalog.routes').then(mod => mod.routes),
    },
    {
        path: 'safemode',
        loadChildren: () => import('../safemode/safemode-deep-link.module').then(mod => mod.SafeModeDeepLinkModule),
    },
    {
        path: 'support',
        loadChildren: () => import('../support/support.module').then(mod => mod.SupportModule),
    },
    {
        path: 'reporting',
        loadChildren: () => import('../reporting/reporting.module').then(mod => mod.ReportingModule),
        data: {
            orgScoped: false,
        },
    },
    {
        // Legacy path from before the 2026-09-17 rename (CLOUD-263953), kept
        // because links to it had already been shared. No `pathMatch: 'full'`, so
        // Angular carries the remaining segments along and /download-center/edi
        // still lands on the EDI detail view.
        //
        // The gates deliberately live only on the target route: running them here
        // too would evaluate them twice and redirect away from the redirect. The
        // route must stay in NON_ORG_SCOPED_ROUTES, or bareRouteMatchGuard rewrites
        // it under /orgs/<uid> before the redirect ever fires.
        path: 'download-center',
        redirectTo: 'entitlements',
        data: { orgScoped: false },
    },
    {
        // Operating-Company-level, like `reporting`: never sits under /orgs/<uid>
        // (see NON_ORG_SCOPED_ROUTES) and the org switcher is disabled on it
        // (see ORG_SWITCHER_READONLY_TAB_IDS in page-wrapper.component.ts).
        //
        // Both guards, in this order: the flag decides whether the page exists at
        // all and redirects to `/` when it does not, and only then does the
        // `navigate` half of `view_download_center` decide whether this user may
        // reach it — the access guard sends them to their own best landing page,
        // which is the wrong answer for a feature nobody has yet.
        //
        // That `navigate` answer comes from the backend, evaluated in the OC's
        // CUSTOMER default org, not from `hasPermissionGuard`: a bare `isAllowed`
        // resolves against the effective org, which on this OC-level page is
        // whichever org was last visited (CLOUD-259859).
        //
        // The flag key and the permission strings keep the pre-rename wording: both
        // are registered outside this repo (the flag service, and
        // authz-directory-seeder#1618), so renaming them is not a GUI-side change.
        path: 'entitlements',
        loadChildren: () => import('../download-center/download-center.routes').then(mod => mod.DOWNLOAD_CENTER_ROUTES),
        canActivate: [isFeaturePolicyEnabled(FeatureNames.DOWNLOAD_CENTER, '/'), downloadCenterAccessGuard],
        data: {
            orgScoped: false,
        },
    },
    {
        path: 'points-of-view',
        loadChildren: () => import('../points-of-view/points-of-view.module').then(mod => mod.PointsOfViewModule),
    },
    {
        path: 'pure4sight',
        redirectTo: 'copilot',
    },
    {
        path: 'copilot',
        loadChildren: () => import('../copilot/copilot.routes').then(mod => mod.routes),
    },
    {
        path: 'empty',
        component: EmptyPageComponent,
        title: '',
        data: {
            ownedBy: PureTeam.DX_SUBMERCE,
            orgScoped: false,
        },
    },
    {
        path: 'notifications',
        loadChildren: () =>
            import('../notification-center/notification-center.module').then(mod => mod.NotificationCenterModule),
        data: {
            orgScoped: false,
        },
    },
    {
        path: 'resource-details',
        loadChildren: () => import('../resource/resource.routes').then(mod => mod.routes),
    },
    {
        path: 'services/servicecatalog/orders',
        redirectTo: 'services/orders',
    },
];

export const routes: Routes = [
    {
        // Org-scoped parent: `/orgs/:orgUid/...` keeps the uid in the URL.
        // canMatch decides based on the org-URL FF whether this branch is live.
        // orgUidValidatorGuard validates the uid against the user's accessible orgs and
        // primes EffectiveOrgService for OrgHeadersInterceptor.
        path: 'orgs/:orgUid',
        component: PageWrapperComponent,
        canMatch: [orgScopedRouteMatchGuard],
        canActivate: [hasPermissionGuard(null), orgUidValidatorGuard],
        children: PAGE_WRAPPER_CHILDREN,
    },
    {
        // Org-unscoped parent: `/...` without uid prefix.
        // bareRouteMatchGuard handles the FF-on case by redirecting org-scoped routes
        // to `/orgs/<uid>/...`. platformOrgPrimerGuard primes EffectiveOrgService from
        // PlatformClientService when the FF is OFF (legacy access-token path). When
        // the FF is ON it no-ops — orgUidValidatorGuard owns priming.
        path: '',
        component: PageWrapperComponent,
        canMatch: [bareRouteMatchGuard],
        canActivate: [hasPermissionGuard(null), platformOrgPrimerGuard],
        children: PAGE_WRAPPER_CHILDREN,
    },
    {
        // Invalid route handling
        // This MUST be here in PageRouting, and MUST be at the end of the list.
        // This redirects to /fleet, and if the user doesn't have access to that page, the hasPermissionGuard will redirect them again to an appropriate page.
        path: '**',
        pathMatch: 'full',
        redirectTo: 'fleet',
    },
];

/**
 * A custom TitleStrategy that just prevents changing the page title at all.
 * We may want to have the page title change in the future, but we need to fully migrate to Angular router first
 * before we can do that through the router, otherwise pages that are not yet migrated won't show the right title
 * (it'll just show whatever the title was previously, and won't match the current page).
 * See: CLOUD-121155
 */
@Injectable({ providedIn: 'root' })
export class CustomTitleStrategy extends TitleStrategy {
    constructor(private readonly title: Title) {
        super();
    }

    override buildTitle(_snapshot: RouterStateSnapshot): string | undefined {
        return undefined;
    }

    override getResolvedTitleForRoute(_snapshot: ActivatedRouteSnapshot): any {
        return null;
    }

    override updateTitle(_snapshot: RouterStateSnapshot): void {}
}

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
    providers: [{ provide: TitleStrategy, useClass: CustomTitleStrategy }],
})
export class PageRoutingModule {}
